import { useState } from 'react';
import Tag from '../components/Tag';
import { CORE_THESIS, OPERATING_RULE, VALUE_CHAIN, NAMING, DEPARTMENTS, KPIS, GLOSSARY } from '../lib/dna';

type Tab = 'overview' | 'chain' | 'depts' | 'naming' | 'kpis' | 'glossary';
const TABS: [Tab, string][] = [
  ['overview', 'Overview'], ['chain', '12-Step Value Chain'], ['depts', 'Departments'],
  ['naming', 'Naming Standard'], ['kpis', 'KPI Framework'], ['glossary', 'Glossary'],
];

export default function Manual() {
  const [tab, setTab] = useState<Tab>('overview');
  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Operating Manual</div>
          <div className="page-sub">MFX OS Internal DNA v1.0 — the operating model, governance, data standards, and AI framework.</div>
        </div>
        <span className="live-flag">DNA v1.0</span>
      </div>

      <div className="tabs">
        {TABS.map(([k, l]) => <button key={k} className={`tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{l}</button>)}
      </div>

      {tab === 'overview' && (
        <div className="grid cols-2">
          <div className="card pad">
            <div className="section-h">Core Thesis</div>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--tx)' }}>{CORE_THESIS}</p>
          </div>
          <div className="card pad">
            <div className="section-h">The Operating Rule</div>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ac)' }}>{OPERATING_RULE}</p>
          </div>
        </div>
      )}

      {tab === 'chain' && (
        <div className="card">
          <div className="card-header"><div><h3>End-to-End Operating Flow</h3><p>Every job follows this 12-step value chain.</p></div></div>
          <div className="card-body">
            <div className="chain">
              {VALUE_CHAIN.map((s) => (
                <div className="chain-step" key={s.n}>
                  <div className="chain-num">{s.n}</div>
                  <div><strong>{s.step}</strong><p>{s.desc}</p><div className="chain-data">{s.data}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'depts' && (
        <div className="card">
          <div className="card-header"><div><h3>Department Workspaces</h3><p>Each functional area has a dedicated workspace.</p></div></div>
          <div className="card-body">
            <div className="grid cols-2">
              {DEPARTMENTS.map((d) => (
                <div className="note-box" key={d.code} style={{ display: 'flex', gap: 12 }}>
                  <div className="dept-code">{d.code}</div>
                  <div><div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}><strong style={{ color: 'var(--tx)' }}>{d.name}</strong><Tag kind="dp" value={d.dept} /></div>{d.focus}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'naming' && (
        <div className="card">
          <div className="card-header"><div><h3>Naming &amp; Numbering Standard</h3><p>Every record uses a canonical, traceable ID.</p></div></div>
          <div className="table-wrap"><table>
            <thead><tr><th>Object</th><th>Format</th><th>Purpose</th></tr></thead>
            <tbody>{NAMING.map((n) => (<tr key={n.object} style={{ cursor: 'default' }}><td><strong>{n.object}</strong></td><td><code className="mono">{n.format}</code></td><td style={{ color: 'var(--tx2)' }}>{n.purpose}</td></tr>))}</tbody>
          </table></div>
        </div>
      )}

      {tab === 'kpis' && (
        <div className="card">
          <div className="card-header"><div><h3>KPI Framework</h3><p>What each area measures and why.</p></div></div>
          <div className="table-wrap"><table>
            <thead><tr><th>Area</th><th>KPI</th><th>Why it matters</th></tr></thead>
            <tbody>{KPIS.map((k) => (<tr key={k.area} style={{ cursor: 'default' }}><td><Tag kind="dp" value={k.dept} /></td><td><strong>{k.kpi}</strong><div className="sub">{k.area}</div></td><td style={{ color: 'var(--tx2)' }}>{k.why}</td></tr>))}</tbody>
          </table></div>
        </div>
      )}

      {tab === 'glossary' && (
        <div className="card">
          <div className="card-header"><div><h3>Glossary</h3><p>Shared vocabulary for the operating model.</p></div></div>
          <div className="card-body">
            {GLOSSARY.map((g) => (
              <div className="field-row" key={g.term} style={{ gridTemplateColumns: '160px 1fr' }}>
                <span className="field-k" style={{ color: 'var(--ac)', fontWeight: 600 }}>{g.term}</span>
                <span className="field-v" style={{ color: 'var(--tx2)' }}>{g.def}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
